import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { flatten, sortBy } from 'lodash';
import moment from 'moment';
import type { Moment } from 'moment';

import { momentWithUserTimezone } from 'js/utils/DateTimeUtils';

import type {
  CalendarEventsMap,
  CalendarEventsMapByFilter,
  CalendarItemsMap,
  CalendarItemsMapByFilter,
  DegreeContextType,
} from 'bundles/degree-home/components/v2/types/DegreeHomeContextTypes';
import type { RegistrationDates } from 'bundles/degree-home/components/v3-dpm/types/calendarTypes';

import { filterOptionValues, filterOptions } from './Calendar';

const emptyCalendarItems = {
  [filterOptionValues.All]: {},
  [filterOptionValues.Deadlines]: {},
  [filterOptionValues.LiveEvents]: {},
  [filterOptionValues.Overdue]: {},
};

// setting a default empty function since setting undefined as a return type here doesn't work
// eslint-disable-next-line @typescript-eslint/no-empty-function
const emptyFunction = () => {};

const DegreeContext = createContext<DegreeContextType>({
  dateInFocus: moment(),
  setDateInFocus: () => undefined,
  calendarItems: emptyCalendarItems,
  setCalendarItemsData: () => undefined,
  calendarItemsLoading: true,
  setCalendarItemsLoading: () => undefined,
  visibleCalendarItems: {},
  setVisibleCalendarItems: () => undefined,
  filter: filterOptions[0],
  setFilter: () => undefined,
  overdueItemsCount: 0,
  setOverdueItemsCount: () => undefined,
  degreeId: '',
  setDegreeId: () => undefined,
  refetchCalendarItems: () => emptyFunction,
  setRefetchCalendarItems: () => undefined,
  refetchRequired: false,
  setRefetchRequired: () => undefined,
  calendarEvents: null,
  setCalendarEvents: () => undefined,
  setVisibleCalendarEvents: () => undefined,
  visibleCalendarEvents: {},
  registrationDates: { registrationStartDate: undefined, registrationEndDate: undefined },
  setRegistrationDates: () => undefined,
});

export const useDegreeContext = () => useContext(DegreeContext);

export type PropsToComponent = {
  children: React.ReactElement | React.ReactNode;
  degreeId: string;
};

export const DegreeContextProvider: React.FC<PropsToComponent> = ({ degreeId: degreeIdProp, children }) => {
  const [dateInFocus, setDateInFocus] = useState<Moment>(momentWithUserTimezone());
  const [calendarItems, setCalendarItemsData] = useState<CalendarItemsMapByFilter>(emptyCalendarItems);
  const [calendarItemsLoading, setCalendarItemsLoading] = useState(true);

  const [calendarEvents, setCalendarEvents] = useState<CalendarEventsMapByFilter | null>(null);

  const [visibleCalendarItems, setVisibleCalendarItems] = useState<CalendarItemsMap>({});
  const [visibleCalendarEvents, setVisibleCalendarEvents] = useState<CalendarEventsMap>({});
  const [filter, setFilter] = useState(filterOptions[0]);

  const [overdueItemsCount, setOverdueItemsCount] = useState(0);

  const [degreeId, setDegreeId] = useState(degreeIdProp);
  const [refetchCalendarItems, setRefetchCalendarItems] = useState(() => emptyFunction);
  const [refetchRequired, setRefetchRequired] = useState(false);
  const [registrationDates, setRegistrationDates] = useState<RegistrationDates>({
    registrationStartDate: undefined,
    registrationEndDate: undefined,
  });

  useEffect(() => {
    if (calendarEvents) {
      const overdueEvents = flatten(Object.values(calendarEvents[filterOptionValues.Overdue]));

      if (filter === filterOptionValues.All) {
        setVisibleCalendarEvents(calendarEvents[filterOptionValues.All]);
      } else if (filter === filterOptionValues.Deadlines) {
        setVisibleCalendarEvents(calendarEvents[filterOptionValues.Deadlines]);
      } else if (filter === filterOptionValues.LiveEvents) {
        setVisibleCalendarEvents(calendarEvents[filterOptionValues.LiveEvents]);
      } else if (filter === filterOptionValues.Overdue) {
        setVisibleCalendarEvents(calendarEvents[filterOptionValues.Overdue]);
        // Start at the oldest overdue item (All overdue items are assignments)
        const sortedOverdueItems = sortBy(Object.keys(calendarEvents[filterOptionValues.Overdue]));
        const oldestOverdueDate = sortedOverdueItems[0];
        setDateInFocus(momentWithUserTimezone(oldestOverdueDate));
      }
      setOverdueItemsCount(overdueEvents.length);
    }

    const overdueItems = flatten(Object.values(calendarItems[filterOptionValues.Overdue]));

    if (filter === filterOptionValues.All) {
      setVisibleCalendarItems(calendarItems[filterOptionValues.All]);
    } else if (filter === filterOptionValues.Deadlines) {
      setVisibleCalendarItems(calendarItems[filterOptionValues.Deadlines]);
    } else if (filter === filterOptionValues.LiveEvents) {
      setVisibleCalendarItems(calendarItems[filterOptionValues.LiveEvents]);
    } else if (filter === filterOptionValues.Overdue) {
      setVisibleCalendarItems(calendarItems[filterOptionValues.Overdue]);
      // Start at the oldest overdue item (All overdue items are assignments)
      const sortedOverdueItems = sortBy(Object.keys(calendarItems[filterOptionValues.Overdue]));
      const oldestOverdueDate = sortedOverdueItems[0];
      setDateInFocus(momentWithUserTimezone(oldestOverdueDate));
    }
    setOverdueItemsCount(overdueItems.length);
  }, [filter, calendarItems, calendarEvents]);

  return (
    <DegreeContext.Provider
      value={{
        dateInFocus,
        setDateInFocus,
        calendarItems,
        setCalendarItemsData,
        calendarItemsLoading,
        setCalendarItemsLoading,
        visibleCalendarItems,
        setVisibleCalendarItems,
        filter,
        setFilter,
        overdueItemsCount,
        setOverdueItemsCount,
        degreeId,
        setDegreeId,
        refetchCalendarItems,
        setRefetchCalendarItems,
        refetchRequired,
        setRefetchRequired,
        setCalendarEvents,
        calendarEvents,
        setVisibleCalendarEvents,
        visibleCalendarEvents,
        registrationDates,
        setRegistrationDates,
      }}
    >
      {children}
    </DegreeContext.Provider>
  );
};

export default DegreeContextProvider;
